/**
 * Per-surface profile-field visibility registry
 * ============================================================================
 *
 * Single source of truth for "which dancer profile fields appear on which
 * surface, for which brand, for which viewer." Render sites consume
 * `isFieldVisible(field, ctx)` instead of hardcoding their own filter rules.
 *
 * Why this exists
 * ---------------
 * A dancer's profile data is the same across surfaces, but the *display*
 * differs along three axes:
 *
 *  1. **Surface** (where is this rendered?) — public dancer profile,
 *     competition snapshot, agency profile, audition snapshot, PDF booklet.
 *  2. **Brand** (which product is the viewer using?) — Flow (career-dancer
 *     site at `flow.ballee.co`) vs Ballee (broader community + agency at
 *     `ballee.co`). The same field can be public on Flow and recruiter-only
 *     on Ballee, or vice versa.
 *  3. **Viewer role** (who is looking?) — anonymous, authenticated peer,
 *     recruiter (any member of `client_members` or `organization_staff`),
 *     profile owner, super admin. Sensitive fields (contact info, weight,
 *     measurements) can be hidden from the general public but exposed to the
 *     recruiter tier, which needs them for casting decisions.
 *
 *     "Recruiter" is a tier, not a seat. It is any client casting-team member or
 *     organization member — there is no separate recruiter role to hold.
 *
 * Without this registry, render sites copy-paste their own conditionals
 * (e.g. "show eye color iff variant === 'public'"), drift over time, and
 * make per-brand / per-audience rules impossible to ship cleanly.
 *
 * Backward compatibility
 * ----------------------
 * The legacy helpers `isFieldVisibleInView(field, view)` and
 * `getFieldsForView(view)` continue to work — they delegate to the new
 * predicate API with a default context (`brand: 'flow', viewer: 'anonymous'`).
 * Existing call sites do not need to change in lockstep with this file;
 * migrate them to `isFieldVisible(field, ctx)` opportunistically when you
 * touch them, or as part of a dedicated wiring phase.
 *
 * How to add a surface
 * --------------------
 *  1. Add the surface to `ViewKey` below.
 *  2. Add an entry to `FIELD_VISIBILITY` listing the fields/media the
 *     surface displays and the rule that gates each.
 *  3. Add tests in `view-fields.test.ts` covering the matrix cells you care
 *     about for that surface.
 *
 * How to add a field
 * ------------------
 *  1. Add the key to `ProfileFieldKey`. Match the `PROFILE_FIELDS`
 *     palette in
 *     `packages/features/auditions/src/components/application-form/application-form-builder.tsx`
 *     when the field is also audition-bindable; the two registries should
 *     stay aligned for fields that exist in both.
 *  2. Add it to each surface in `FIELD_VISIBILITY` that should display it,
 *     pairing it with the right rule (`ALWAYS`, `OWNER_OR_RECRUITER`, etc.).
 *  3. Add a test for the field's visibility per surface / brand / viewer.
 *
 * Per-dancer privacy toggles (shipped 2026-05-13)
 * -----------------------------------------------
 * The dancer can further restrict what's shown publicly via five booleans
 * on `professional_profiles`:
 *   bio_public, measurements_public, contact_public, social_public,
 *   resume_public.
 *
 * AND-only semantics: the dancer's setting AND-gates against the
 * registry's role-based ceiling. It can further restrict but never
 * override. `measurements_public = TRUE` on a public Ballee profile
 * does NOT expose weight to anon viewers — the registry keeps the body
 * measurement fields recruiter-only.
 *
 * Callers pass these as the optional `dancerPreferences` arg on
 * `isFieldVisible` / `isMediaVisible`. Admin viewers bypass the
 * preferences for moderation; everyone else (including the owner viewing
 * their own public preview) is subject to them.
 *
 * Out of scope (future work)
 * --------------------------
 *  - Per-org overrides for view rules (some agencies might want height
 *    visible, others not). Would extend `ViewContext` with `orgId?`.
 *  - Localized field labels. Today the registry only decides visibility;
 *    labels stay in i18n.
 */

// ==================== Types ====================

/**
 * Profile fields that view rules can include or exclude. The union covers
 * every column the public dancer profile reads from `profiles`,
 * `professional_profiles`, and `artist_profiles`. New columns added to
 * those tables don't appear here automatically — adding them is part of
 * the migration that makes them displayable.
 *
 * Alignment with audition forms: keys that are also audition-bindable
 * MUST match the `PROFILE_FIELDS` palette in
 * `packages/features/auditions/src/components/application-form/application-form-builder.tsx`.
 * Drift between the two breaks the audition prefill/backfill loop.
 */
export type ProfileFieldKey =
  // Identity
  | 'firstName'
  | 'lastName'
  | 'profilePhotoUrl'
  | 'gender'
  | 'birthdate'
  | 'nationalities'
  | 'languages'
  // Location
  | 'city'
  | 'countryOfResidence'
  // Contact (rarely shown publicly)
  | 'contactEmail'
  | 'contactPhone'
  // Professional
  | 'bio'
  | 'yearsOfExperience'
  | 'danceStyles'
  | 'danceLevel'
  | 'availableForHire'
  | 'lookingFor'
  // Physical — dimensions
  | 'heightCm'
  | 'weightKg'
  | 'bustCm'
  | 'waistCm'
  | 'hipsCm'
  | 'clothingSize'
  | 'shoeSize'
  | 'pointeShoeBrand'
  | 'pointeShoeSize'
  // Physical — appearance (free-text, Meteor-parity)
  | 'eyeColor'
  | 'hairColor'
  | 'skinTone'
  // Social
  | 'instagramHandle'
  | 'tiktokHandle'
  | 'facebookUsername'
  | 'youtubeUsername'
  // The remaining four social columns on `professional_profiles`. They were
  // absent from this union until 2026-07-30, which is why `snapchat_handle`
  // shipped ungated on the public dancer loaders while its four siblings were
  // gated — the registry had no key to hang `social_public` on.
  | 'vimeoHandle'
  | 'pinterestHandle'
  | 'snapchatHandle'
  | 'xHandle'
  | 'website'
  | 'portfolioUrl'
  // Availability
  | 'geographicAvailability'
  // Meteor-parity sections (not column-backed; gating only)
  | 'featuredEvents'
  | 'resumeWidget'
  // Fever-directory cast signals (derived, NOT column-backed on the profile).
  // These are the "cast-frequency" data Philippine flagged: peers must never
  // see them (BAL-316, R4). They appear ONLY on the fever_directory surface
  // and are gated FEVER_OR_ADMIN — a peer/public/self viewer never gets them
  // through the registry, and the data itself only flows from the self-gated
  // get_fever_cast_metrics() RPC. Kept in this union so the registry is the
  // single place that enumerates them; the leak-guard test asserts this set.
  | 'totalFeverShows'
  | 'lastCastDate'
  | 'availabilityNext30d'
  | 'distanceFromTarget'
  | 'profileCompleteness';

/**
 * Media types attached to a dancer's profile. Independent of
 * `ProfileFieldKey` because media has its own storage + RLS path
 * (`artist_media` and `profile_media` tables).
 */
export type MediaTypeKey =
  | 'headshot'
  | 'photos'
  | 'videos'
  | 'resume'
  | 'arabesque'
  | 'balletClass'
  | 'classicalVariation'
  | 'modernVariation';

/**
 * The named surfaces that consume profile data. Each maps to a
 * `FieldVisibilityRule` in `FIELD_VISIBILITY`.
 *
 * - `public_dancer_profile`: `/artists/[slug]` — the dancer's "shop window."
 * - `agency_dancer_profile`: future agency feature; placeholder rules.
 * - `competition_snapshot`: PDF booklet / organizer view sent to a
 *    competition. Decision (2026-05-02): NO measurements, NO eye/hair/
 *    skin color, but YES height (industry standard for dance).
 * - `audition_snapshot`: per-audition `application_structure` decides;
 *    NOT registry-driven. Included for completeness.
 * - `dancer_booklet_pdf`: the existing `dancer-booklet-template.tsx` PDF.
 *    Currently inherits public-profile rules; may diverge later.
 */
export type ViewKey =
  | 'public_dancer_profile'
  | 'agency_dancer_profile'
  | 'competition_snapshot'
  | 'audition_snapshot'
  | 'dancer_booklet_pdf'
  // Fever-staff recruitment directory (`/home/(user)/fever-directory`,
  // BAL-316). Fever client-org staff + super admins only. The only surface
  // that exposes the cast-signal fields, and only to fever_staff / admin.
  | 'fever_directory';

/**
 * Brand currently rendering the surface. Sourced from `apps/web/lib/brand.ts`
 * `getBrand()` (middleware-set `x-brand` header).
 */
export type Brand = 'flow' | 'ballee';

/**
 * Viewer role relative to the profile being displayed.
 *
 *  - `anonymous`: no auth session.
 *  - `authenticated`: signed in but no privileged relation to the profile.
 *  - `recruiter`: the recruiter tier — any member of `client_members` for any
 *     client, OR any member of `organization_staff` for any organization.
 *     Membership alone qualifies; there is no recruiter seat to hold and no
 *     per-role check behind it.
 *  - `owner`: viewing their own profile.
 *  - `admin`: super admin (`is_super_admin()` returns true).
 *  - `fever_staff`: member of a Fever client organization viewing the Fever
 *     recruitment directory. Sees cast-signal fields (total Fever shows, last
 *     cast date, ...). Determined server-side via `is_fever_client_user()`;
 *     never client-passed (BAL-316).
 *
 * Determination logic lives in `viewer-role.ts` (added in the wiring phase).
 */
export type ViewerRole =
  | 'anonymous'
  | 'authenticated'
  | 'recruiter'
  | 'owner'
  | 'admin'
  | 'fever_staff';

/**
 * Full context handed to a visibility rule. Surfaces look up rules by
 * `surface`; rules can branch on `brand` and `viewer`.
 */
export interface ViewContext {
  surface: ViewKey;
  brand: Brand;
  viewer: ViewerRole;
}

/**
 * Per-dancer privacy toggles. AND-gated against the registry's
 * role-based ceiling — see the module-level "Per-dancer privacy
 * toggles" comment.
 *
 * Null / undefined is treated per the DB column default:
 *   bio_public          → true   (legacy rows render bio)
 *   social_public       → true   (legacy rows render socials)
 *   resume_public       → true   (legacy rows render resume)
 *   measurements_public → false  (legacy rows hide measurements)
 *   contact_public      → false  (legacy rows hide contact)
 */
export interface DancerVisibilityPreferences {
  bio_public?: boolean | null;
  social_public?: boolean | null;
  resume_public?: boolean | null;
  measurements_public?: boolean | null;
  contact_public?: boolean | null;
}

const PREFERENCE_DEFAULTS: Record<keyof DancerVisibilityPreferences, boolean> =
  {
    bio_public: true,
    social_public: true,
    resume_public: true,
    measurements_public: false,
    contact_public: false,
  };

/**
 * Field → preference toggle map. Fields not listed here have no
 * per-dancer preference — they pass through the registry unchanged.
 */
const FIELD_PREFERENCE_KEY: Partial<
  Record<ProfileFieldKey, keyof DancerVisibilityPreferences>
> = {
  bio: 'bio_public',
  contactEmail: 'contact_public',
  contactPhone: 'contact_public',
  heightCm: 'measurements_public',
  weightKg: 'measurements_public',
  bustCm: 'measurements_public',
  waistCm: 'measurements_public',
  hipsCm: 'measurements_public',
  clothingSize: 'measurements_public',
  shoeSize: 'measurements_public',
  pointeShoeBrand: 'measurements_public',
  pointeShoeSize: 'measurements_public',
  instagramHandle: 'social_public',
  tiktokHandle: 'social_public',
  facebookUsername: 'social_public',
  youtubeUsername: 'social_public',
  vimeoHandle: 'social_public',
  pinterestHandle: 'social_public',
  snapchatHandle: 'social_public',
  xHandle: 'social_public',
  website: 'social_public',
  portfolioUrl: 'social_public',
};

const MEDIA_PREFERENCE_KEY: Partial<
  Record<MediaTypeKey, keyof DancerVisibilityPreferences>
> = {
  resume: 'resume_public',
};

// ==================== Physical column backing ====================

/**
 * The three profile tables a `ProfileFieldKey` can be stored on.
 */
export type GatedColumnTable =
  | 'profiles'
  | 'professional_profiles'
  | 'artist_profiles';

export interface GatedColumn {
  table: GatedColumnTable;
  column: string;
}

/**
 * `ProfileFieldKey` → the physical column(s) it reads from.
 *
 * Why this is separate from `FIELD_PREFERENCE_KEY`: the registry keys are
 * camelCase display concepts, but every egress path (a SELECT projection, an
 * RSC flight payload, an AI tool response) deals in snake_case columns. Without
 * this table the toggle map cannot be checked against a projection, which is
 * how nine leaks of the same class shipped one at a time.
 *
 * Only fields that carry a per-dancer toggle need an entry. A key mapped to an
 * empty list is a deliberate "no column backs this" statement, not an omission.
 *
 * The camelCase key does NOT imply the column name. Two known divergences:
 *
 *  - `facebookUsername` / `youtubeUsername` each back TWO real columns on
 *    `professional_profiles`. `facebook_username` / `youtube_username` are the
 *    Meteor-import columns still read by `public-dancer-profile.service.ts`;
 *    `facebook_handle` / `youtube_handle` are what the dancer's own edit
 *    surface (`social-section.tsx`) and `atomic_profile_update` write, and what
 *    the `/dancers/[slug]` loaders read. Both are live, so both are gated —
 *    deriving one name from the registry key would have left the other open.
 *  - `contactEmail` has no column on any of the three tables. The public
 *    surface emits `contact_email: null` by construction; the address lives on
 *    `auth.users`, which is not anon-readable.
 */
const FIELD_COLUMNS: Partial<Record<ProfileFieldKey, readonly GatedColumn[]>> =
  {
    bio: [{ table: 'professional_profiles', column: 'bio' }],
    // No column backing — see the note above.
    contactEmail: [],
    contactPhone: [{ table: 'profiles', column: 'phone_number' }],
    heightCm: [{ table: 'artist_profiles', column: 'height_cm' }],
    weightKg: [{ table: 'artist_profiles', column: 'weight_kg' }],
    bustCm: [{ table: 'artist_profiles', column: 'bust_cm' }],
    waistCm: [{ table: 'artist_profiles', column: 'waist_cm' }],
    hipsCm: [{ table: 'artist_profiles', column: 'hips_cm' }],
    clothingSize: [{ table: 'artist_profiles', column: 'clothing_size' }],
    shoeSize: [
      { table: 'artist_profiles', column: 'shoe_size' },
      { table: 'artist_profiles', column: 'shoe_size_us' },
    ],
    pointeShoeBrand: [
      { table: 'artist_profiles', column: 'pointe_shoe_brand' },
    ],
    pointeShoeSize: [{ table: 'artist_profiles', column: 'pointe_shoe_size' }],
    instagramHandle: [
      { table: 'professional_profiles', column: 'instagram_handle' },
    ],
    tiktokHandle: [{ table: 'professional_profiles', column: 'tiktok_handle' }],
    facebookUsername: [
      { table: 'professional_profiles', column: 'facebook_handle' },
      { table: 'professional_profiles', column: 'facebook_username' },
    ],
    youtubeUsername: [
      { table: 'professional_profiles', column: 'youtube_handle' },
      { table: 'professional_profiles', column: 'youtube_username' },
    ],
    vimeoHandle: [{ table: 'professional_profiles', column: 'vimeo_handle' }],
    pinterestHandle: [
      { table: 'professional_profiles', column: 'pinterest_handle' },
    ],
    snapchatHandle: [
      { table: 'professional_profiles', column: 'snapchat_handle' },
    ],
    xHandle: [{ table: 'professional_profiles', column: 'x_handle' }],
    website: [{ table: 'professional_profiles', column: 'website' }],
    portfolioUrl: [{ table: 'professional_profiles', column: 'portfolio_url' }],
  };

function emptyToggleIndex(): Record<
  keyof DancerVisibilityPreferences,
  GatedColumn[]
> {
  return {
    bio_public: [],
    measurements_public: [],
    contact_public: [],
    social_public: [],
    resume_public: [],
  };
}

/**
 * Toggle → every physical column that toggle governs.
 *
 * DERIVED, never hand-written: the toggle assignment comes from
 * `FIELD_PREFERENCE_KEY` and the column names from `FIELD_COLUMNS`, so a field
 * cannot be gated by one map and ungated by the other (R4). `resume_public`
 * governs media rows in `artist_media`, not a profile column, so its list is
 * legitimately empty.
 */
export const GATED_COLUMNS_BY_TOGGLE: Readonly<
  Record<keyof DancerVisibilityPreferences, readonly GatedColumn[]>
> = (() => {
  const index = emptyToggleIndex();
  for (const [field, toggle] of Object.entries(FIELD_PREFERENCE_KEY) as [
    ProfileFieldKey,
    keyof DancerVisibilityPreferences,
  ][]) {
    for (const col of FIELD_COLUMNS[field] ?? []) {
      index[toggle].push(col);
    }
  }
  return index;
})();

/** Every gated column, flattened. */
export const GATED_COLUMNS: readonly GatedColumn[] = Object.values(
  GATED_COLUMNS_BY_TOGGLE,
).flat();

/**
 * Which toggle governs a column, by column name alone.
 *
 * Column names are unique across the three profile tables for every gated
 * field, so callers that only have a projection string (the egress detector,
 * a code review) can resolve the toggle without knowing the table.
 */
export function toggleForColumn(
  column: string,
): keyof DancerVisibilityPreferences | null {
  for (const [toggle, cols] of Object.entries(GATED_COLUMNS_BY_TOGGLE) as [
    keyof DancerVisibilityPreferences,
    readonly GatedColumn[],
  ][]) {
    if (cols.some((c) => c.column === column)) return toggle;
  }
  return null;
}

/**
 * Every `ProfileFieldKey` that carries a toggle, paired with that toggle.
 * Exposed so the detector can also match camelCase emission keys — AI tools
 * and mappers rename `height_cm` to `heightCm` on the way out, and that
 * rename is where several of the leaks lived.
 */
export const GATED_FIELD_KEYS: Readonly<
  Partial<Record<ProfileFieldKey, keyof DancerVisibilityPreferences>>
> = FIELD_PREFERENCE_KEY;

function resolvePreference(
  prefs: DancerVisibilityPreferences | undefined,
  key: keyof DancerVisibilityPreferences,
): boolean {
  const v = prefs?.[key];
  return v ?? PREFERENCE_DEFAULTS[key];
}

/**
 * A predicate that decides whether a given field/media is visible in a
 * given (brand, viewer) pair on its surface. Surfaces don't pass `surface`
 * to the rule because the rule already lives under that surface in
 * `FIELD_VISIBILITY`.
 */
export type FieldRule = (ctx: Pick<ViewContext, 'brand' | 'viewer'>) => boolean;

export interface FieldVisibilityRule {
  fields: Partial<Record<ProfileFieldKey, FieldRule>>;
  media: Partial<Record<MediaTypeKey, FieldRule>>;
}

// ==================== Rule helpers ====================

/** Always visible. */
export const ALWAYS: FieldRule = () => true;

/** Never visible (used to make the table self-documenting). */
export const NEVER: FieldRule = () => false;

/** Owner or super admin. */
export const ONLY_OWNER: FieldRule = (ctx) =>
  ctx.viewer === 'owner' || ctx.viewer === 'admin';

/** Owner, super admin, or any recruiter (client or organization member). */
export const OWNER_OR_RECRUITER: FieldRule = (ctx) =>
  ctx.viewer === 'owner' ||
  ctx.viewer === 'admin' ||
  ctx.viewer === 'recruiter';

/** Authenticated viewers only (any logged-in user). */
export const AUTHENTICATED_ONLY: FieldRule = (ctx) =>
  ctx.viewer !== 'anonymous';

/**
 * Fever staff or super admin ONLY. The gate for cast-signal fields on the
 * fever_directory surface (BAL-316, R4). Every other viewer — including a
 * dancer peer, an owner, or the anonymous public — is denied. This is the
 * registry half of the "no peer cast count anywhere" lock; the data half is
 * the self-gated get_fever_cast_metrics() RPC.
 */
export const FEVER_OR_ADMIN: FieldRule = (ctx) =>
  ctx.viewer === 'fever_staff' || ctx.viewer === 'admin';

/** Restrict to a specific brand. */
export const onlyOnBrand =
  (brand: Brand): FieldRule =>
  (ctx) =>
    ctx.brand === brand;

/** Boolean OR of multiple rules. */
export const anyOf =
  (...rules: FieldRule[]): FieldRule =>
  (ctx) =>
    rules.some((r) => r(ctx));

/**
 * Default context used by the legacy helpers (`isFieldVisibleInView`,
 * `getFieldsForView`). Picks the most-restrictive viewer (`anonymous`)
 * on Flow, which matches the original "is this in the set?" behavior for
 * fields that were always-public.
 */
const DEFAULT_CTX: Pick<ViewContext, 'brand' | 'viewer'> = {
  brand: 'flow',
  viewer: 'anonymous',
};

// ==================== Field sets (composable) ====================

const IDENTITY_FIELDS: ProfileFieldKey[] = [
  'firstName',
  'lastName',
  'profilePhotoUrl',
  'gender',
  'birthdate',
  'nationalities',
  'languages',
];

const LOCATION_FIELDS: ProfileFieldKey[] = ['city', 'countryOfResidence'];

const PROFESSIONAL_FIELDS: ProfileFieldKey[] = [
  'bio',
  'yearsOfExperience',
  'danceStyles',
  'danceLevel',
  'availableForHire',
  'lookingFor',
];

const DIMENSION_FIELDS: ProfileFieldKey[] = [
  'heightCm',
  'weightKg',
  'bustCm',
  'waistCm',
  'hipsCm',
  'clothingSize',
  'shoeSize',
  'pointeShoeBrand',
  'pointeShoeSize',
];

const APPEARANCE_FIELDS: ProfileFieldKey[] = [
  'eyeColor',
  'hairColor',
  'skinTone',
];

const SOCIAL_FIELDS: ProfileFieldKey[] = [
  'instagramHandle',
  'tiktokHandle',
  'facebookUsername',
  'youtubeUsername',
  'vimeoHandle',
  'pinterestHandle',
  'snapchatHandle',
  'xHandle',
  'website',
  'portfolioUrl',
];

const AVAILABILITY_FIELDS: ProfileFieldKey[] = ['geographicAvailability'];

/**
 * Cast-signal fields — the Fever recruitment metrics. Enumerated here so the
 * registry is the single source that knows what "cast frequency data" means
 * (BAL-316, R4). The leak-guard test imports this set to prove every one of
 * them is FEVER_OR_ADMIN-gated and stripped for peer/public/self scopes.
 */
export const CAST_SIGNAL_FIELDS: ProfileFieldKey[] = [
  'totalFeverShows',
  'lastCastDate',
  'availabilityNext30d',
  'distanceFromTarget',
  'profileCompleteness',
];

const ALL_PUBLIC_MEDIA: MediaTypeKey[] = [
  'headshot',
  'photos',
  'videos',
  'arabesque',
  'balletClass',
  'classicalVariation',
  'modernVariation',
];

/**
 * Build a rules map by pairing every key in `keys` with the same rule.
 * Used to keep large surface definitions readable.
 */
function withRule<K extends string>(
  keys: readonly K[],
  rule: FieldRule,
): Partial<Record<K, FieldRule>> {
  return Object.fromEntries(keys.map((k) => [k, rule])) as Partial<
    Record<K, FieldRule>
  >;
}

// ==================== Visibility map ====================

export const FIELD_VISIBILITY: Readonly<Record<ViewKey, FieldVisibilityRule>> =
  {
    /**
     * Public dancer profile (`/artists/[slug]`).
     *
     * Audience tightening (Phase 5):
     *  - Body measurements (weight + bust/waist/hips) — sensitive
     *    physical data — are visible to OWNER + ADMIN + the RECRUITER tier
     *    only. Anonymous and authenticated peers see height + appearance,
     *    not the full measurement sheet.
     *  - Contact info (email + phone) is OWNER_OR_RECRUITER. Anyone on a
     *    client casting team or in an organization needs to reach the
     *    dancer; the general public should not be scraping personal
     *    contact details.
     *  - Everything else (identity, professional details, dance styles,
     *    height, eye/hair/skin, social handles, availability) stays
     *    publicly visible — these are credentials the dancer wants the
     *    world to see.
     *
     * Brand divergence (Flow vs Ballee) is not yet rule-coded — both
     * brands currently apply the same matrix on this surface. Per-brand
     * predicates can be added field-by-field as business rules emerge
     * (e.g. Ballee may surface contact info to verified agencies).
     */
    public_dancer_profile: {
      fields: {
        ...withRule(IDENTITY_FIELDS, ALWAYS),
        ...withRule(LOCATION_FIELDS, ALWAYS),
        ...withRule(PROFESSIONAL_FIELDS, ALWAYS),
        // Height stays public; weight + body measurements tighten.
        heightCm: ALWAYS,
        weightKg: OWNER_OR_RECRUITER,
        bustCm: OWNER_OR_RECRUITER,
        waistCm: OWNER_OR_RECRUITER,
        hipsCm: OWNER_OR_RECRUITER,
        // Sizes stay public — useful for casting calls without being
        // the kind of body data dancers usually keep private.
        clothingSize: ALWAYS,
        shoeSize: ALWAYS,
        pointeShoeBrand: ALWAYS,
        pointeShoeSize: ALWAYS,
        // Appearance — opt-in via the wizard, public for everyone who
        // sees the profile.
        ...withRule(APPEARANCE_FIELDS, ALWAYS),
        // Contact info — recruiter tier / owner / admin only.
        contactEmail: OWNER_OR_RECRUITER,
        contactPhone: OWNER_OR_RECRUITER,
        // Public credentials.
        ...withRule(SOCIAL_FIELDS, ALWAYS),
        ...withRule(AVAILABILITY_FIELDS, ALWAYS),
        // Meteor-parity sections. Always-on at the registry level; the
        // page hides them when their underlying data is empty.
        featuredEvents: ALWAYS,
        resumeWidget: ALWAYS,
      },
      media: withRule([...ALL_PUBLIC_MEDIA, 'resume' as const], ALWAYS),
    },

    /**
     * Agency dancer profile — forthcoming surface. Until the agency
     * module is scoped, mirror the public profile so the helpers have a
     * stable answer. Agencies typically need contact info that the
     * public profile hides; expose `contactPhone` here as a placeholder.
     */
    agency_dancer_profile: {
      fields: {
        ...withRule(IDENTITY_FIELDS, ALWAYS),
        ...withRule(LOCATION_FIELDS, ALWAYS),
        ...withRule(PROFESSIONAL_FIELDS, ALWAYS),
        ...withRule(DIMENSION_FIELDS, ALWAYS),
        ...withRule(APPEARANCE_FIELDS, ALWAYS),
        ...withRule(SOCIAL_FIELDS, ALWAYS),
        ...withRule(AVAILABILITY_FIELDS, ALWAYS),
        contactPhone: ALWAYS,
        featuredEvents: ALWAYS,
        resumeWidget: ALWAYS,
      },
      media: withRule([...ALL_PUBLIC_MEDIA, 'resume' as const], ALWAYS),
    },

    /**
     * Competition snapshot — locked rules per WIP_closing_audition_field_gaps:
     * height YES, measurements NO, eye/hair/skin NO. Industry-standard
     * for dance competition booklets.
     */
    competition_snapshot: {
      fields: {
        firstName: ALWAYS,
        lastName: ALWAYS,
        profilePhotoUrl: ALWAYS,
        gender: ALWAYS,
        birthdate: ALWAYS,
        nationalities: ALWAYS,
        languages: ALWAYS,
        city: ALWAYS,
        countryOfResidence: ALWAYS,
        bio: ALWAYS,
        yearsOfExperience: ALWAYS,
        danceStyles: ALWAYS,
        danceLevel: ALWAYS,
        heightCm: ALWAYS, // explicit YES — industry standard
      },
      media: {
        headshot: ALWAYS,
        videos: ALWAYS,
      },
    },

    /**
     * Audition snapshot — driven by per-audition `application_structure`.
     * The registry doesn't enumerate; included so callers can detect
     * intent and route to the form-driven path.
     */
    audition_snapshot: {
      fields: {},
      media: {},
    },

    /**
     * PDF booklet template currently mirrors public-profile rules.
     * Diverging from public-profile is a deliberate future decision
     * when the booklet's design needs change.
     */
    dancer_booklet_pdf: {
      fields: {
        ...withRule(IDENTITY_FIELDS, ALWAYS),
        ...withRule(LOCATION_FIELDS, ALWAYS),
        ...withRule(PROFESSIONAL_FIELDS, ALWAYS),
        ...withRule(DIMENSION_FIELDS, ALWAYS),
        ...withRule(APPEARANCE_FIELDS, ALWAYS),
        ...withRule(SOCIAL_FIELDS, ALWAYS),
        ...withRule(AVAILABILITY_FIELDS, ALWAYS),
      },
      media: withRule([...ALL_PUBLIC_MEDIA, 'resume' as const], ALWAYS),
    },

    /**
     * Fever recruitment directory (`/home/(user)/fever-directory`, BAL-316).
     *
     * Card fields (Q3): name + photo (identity), city + country (location),
     * languages, dance level, availability — all visible to fever_staff.
     * The cast-signal fields (total Fever shows, last cast date, 30-day
     * availability, distance, completeness) are FEVER_OR_ADMIN: a fever_staff
     * or super-admin viewer sees them; a dancer peer / owner / anonymous
     * viewer NEVER does. profile_category is deliberately absent from this
     * surface (Q5 — never shown at Fever).
     *
     * Note: absence of a field key here means "not on this surface." The
     * Discover tab renders the same card MINUS the cast signals by computing
     * the field set under the caller's scope (dancer_peer/public strip them).
     */
    fever_directory: {
      fields: {
        // Identity subset — no birthdate/gender/nationalities on the card.
        firstName: ALWAYS,
        lastName: ALWAYS,
        profilePhotoUrl: ALWAYS,
        languages: ALWAYS,
        // Location.
        ...withRule(LOCATION_FIELDS, ALWAYS),
        // Professional signal — skill level yes, NOT profile_category (Q5).
        danceStyles: ALWAYS,
        danceLevel: ALWAYS,
        availableForHire: ALWAYS,
        ...withRule(AVAILABILITY_FIELDS, ALWAYS),
        // Cast signals — Fever staff / super admin only (R4).
        ...withRule(CAST_SIGNAL_FIELDS, FEVER_OR_ADMIN),
      },
      media: {
        headshot: ALWAYS,
      },
    },
  };

// ==================== Helpers ====================

/**
 * Predicate: is this field visible on the given surface, brand, and
 * viewer role? This is the canonical visibility check — prefer it over
 * `isFieldVisibleInView` in new code.
 *
 *  - For `audition_snapshot`, returns `false` for every field because
 *    audition rendering is driven by `application_structure`, not the
 *    view registry. Callers needing audition-specific visibility should
 *    consult the audition's application form configuration directly.
 */
export function isFieldVisible(
  field: ProfileFieldKey,
  ctx: ViewContext,
  dancerPreferences?: DancerVisibilityPreferences,
): boolean {
  const rule = FIELD_VISIBILITY[ctx.surface].fields[field];
  if (!rule || !rule(ctx)) return false;
  // When preferences are omitted entirely, the caller is opting out of
  // the per-dancer AND-gate — registry decides alone (back-compat with
  // every call site that predates the 2026-05-13 toggles).
  if (dancerPreferences === undefined) return true;
  // Admin viewers bypass per-dancer toggles for moderation.
  if (ctx.viewer === 'admin') return true;
  const prefKey = FIELD_PREFERENCE_KEY[field];
  if (!prefKey) return true;
  return resolvePreference(dancerPreferences, prefKey);
}

/**
 * Predicate: is this media type visible on the given surface, brand, and
 * viewer role? AND-gated with the dancer's per-section toggle when
 * `dancerPreferences` is supplied (see `DancerVisibilityPreferences`).
 * Omitting `dancerPreferences` preserves legacy behaviour (registry only).
 */
export function isMediaVisible(
  media: MediaTypeKey,
  ctx: ViewContext,
  dancerPreferences?: DancerVisibilityPreferences,
): boolean {
  const rule = FIELD_VISIBILITY[ctx.surface].media[media];
  if (!rule || !rule(ctx)) return false;
  if (dancerPreferences === undefined) return true;
  if (ctx.viewer === 'admin') return true;
  const prefKey = MEDIA_PREFERENCE_KEY[media];
  if (!prefKey) return true;
  return resolvePreference(dancerPreferences, prefKey);
}

/**
 * Legacy helper — checks visibility on a surface using the default
 * context (`brand: 'flow', viewer: 'anonymous'`). Kept for backward
 * compatibility with call sites that haven't been migrated to
 * `isFieldVisible(field, ctx)`. New code should use `isFieldVisible`.
 */
export function isFieldVisibleInView(
  field: ProfileFieldKey,
  view: ViewKey,
): boolean {
  return isFieldVisible(field, { surface: view, ...DEFAULT_CTX });
}

/**
 * Legacy helper — see `isFieldVisibleInView`.
 */
export function isMediaVisibleInView(
  media: MediaTypeKey,
  view: ViewKey,
): boolean {
  return isMediaVisible(media, { surface: view, ...DEFAULT_CTX });
}

/**
 * Return the field keys visible on a given surface under a given context.
 */
export function getFieldsForViewWithContext(
  ctx: ViewContext,
): ProfileFieldKey[] {
  return (
    Object.keys(FIELD_VISIBILITY[ctx.surface].fields) as ProfileFieldKey[]
  ).filter((field) => isFieldVisible(field, ctx));
}

/**
 * Legacy helper — returns fields visible on the surface under the
 * default context.
 */
export function getFieldsForView(view: ViewKey): ProfileFieldKey[] {
  return getFieldsForViewWithContext({ surface: view, ...DEFAULT_CTX });
}

/**
 * Return the media keys visible on a given surface under a given context.
 */
export function getMediaForViewWithContext(ctx: ViewContext): MediaTypeKey[] {
  return (
    Object.keys(FIELD_VISIBILITY[ctx.surface].media) as MediaTypeKey[]
  ).filter((media) => isMediaVisible(media, ctx));
}

/**
 * Legacy helper — returns media visible on the surface under the
 * default context.
 */
export function getMediaForView(view: ViewKey): MediaTypeKey[] {
  return getMediaForViewWithContext({ surface: view, ...DEFAULT_CTX });
}
